import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Custom scrollbar styles for neon red theme - using CSS custom properties
const scrollbarStyles = `
  .modal-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #ef4444 transparent;
  }
  
  .modal-scrollbar::-webkit-scrollbar {
    width: 12px;
  }
  
  .modal-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 6px;
  }
  
  .modal-scrollbar::-webkit-scrollbar-thumb {
    background: #ef4444;
    border-radius: 6px;
    border: 2px solid rgba(0, 0, 0, 0.1);
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3), 0 0 10px rgba(239, 68, 68, 0.6);
  }
  
  .modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #f87171;
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3), 0 0 15px rgba(239, 68, 68, 0.8);
  }
  
  .modal-scrollbar::-webkit-scrollbar-thumb:active {
    background: #dc2626;
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3), 0 0 20px rgba(239, 68, 68, 1);
  }
  
  .modal-scrollbar::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
  const scrollableRef = useRef<HTMLDivElement>(null);

  // Inject custom scrollbar styles globally
  useEffect(() => {
    const styleId = "modal-scrollbar-styles";

    // Remove any existing styles first
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create and inject new styles
    const styleElement = document.createElement("style");
    styleElement.id = styleId;
    styleElement.textContent = scrollbarStyles;
    styleElement.setAttribute("data-scrollbar", "neon-red");
    document.head.appendChild(styleElement);

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  // Apply scrollbar styles directly to the element
  useEffect(() => {
    if (scrollableRef.current) {
      const element = scrollableRef.current;

      // Apply inline styles as fallback
      element.style.setProperty("--scrollbar-thumb-color", "#ef4444");
      element.style.setProperty(
        "--scrollbar-track-color",
        "rgba(0, 0, 0, 0.2)"
      );

      // Force reflow to ensure styles are applied
      element.offsetHeight;
    }
  }, [isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store the current scroll position
      const scrollY = window.scrollY;

      // Get the main scroll container
      const mainScrollContainer = document.getElementById("bg-scroll");

      // Lock the main scroll container by making it fixed
      if (mainScrollContainer) {
        mainScrollContainer.style.position = "fixed";
        mainScrollContainer.style.top = `-${scrollY}px`;
        mainScrollContainer.style.left = "0";
        mainScrollContainer.style.right = "0";
        mainScrollContainer.style.width = "100%";
        mainScrollContainer.style.overflow = "hidden";
      }

      // Lock the body
      document.body.style.overflow = "hidden";

      // Prevent all scroll events from propagating
      const preventScroll = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };

      // Add event listeners to prevent scrolling on the main container
      if (mainScrollContainer) {
        mainScrollContainer.addEventListener("wheel", preventScroll, {
          passive: false,
        });
        mainScrollContainer.addEventListener("touchmove", preventScroll, {
          passive: false,
        });
        mainScrollContainer.addEventListener("scroll", preventScroll, {
          passive: false,
        });
      }

      // Also prevent on document
      document.addEventListener("wheel", preventScroll, { passive: false });
      document.addEventListener("touchmove", preventScroll, { passive: false });
      document.addEventListener("scroll", preventScroll, { passive: false });

      return () => {
        // Remove event listeners
        if (mainScrollContainer) {
          mainScrollContainer.removeEventListener("wheel", preventScroll);
          mainScrollContainer.removeEventListener("touchmove", preventScroll);
          mainScrollContainer.removeEventListener("scroll", preventScroll);
        }

        document.removeEventListener("wheel", preventScroll);
        document.removeEventListener("touchmove", preventScroll);
        document.removeEventListener("scroll", preventScroll);

        // Restore main scroll container
        if (mainScrollContainer) {
          mainScrollContainer.style.position = "";
          mainScrollContainer.style.top = "";
          mainScrollContainer.style.left = "";
          mainScrollContainer.style.right = "";
          mainScrollContainer.style.width = "";
          mainScrollContainer.style.overflow = "auto";
        }

        // Restore body
        document.body.style.overflow = "";

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200]"
          data-modal="true"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
          }}
        >
          <div
            className="h-full flex items-center justify-center p-4 sm:p-8 overflow-y-auto"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ scale: 0.9, y: -30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: -30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto modal-scrollbar"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#ef4444 #transparent",
              }}
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {children}

              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-crimson-400 hover:text-crimson-300 transition-all duration-300 bg-black/60 backdrop-blur-sm border border-crimson-500/30 rounded-full p-2.5 z-10 hover:border-crimson-400/60 hover:shadow-[0_0_15px_rgba(239,68,68,0.4)] hover:bg-black/80 group"
                aria-label="Close modal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;
